/**
 * The Konrad system — a tournament form made for club nights.
 *
 * Created at Hammerfest Sjakklubb in the early 1980s, because players in the club's round
 * robin kept sending apologies and their opponents went weeks without a game, and Monrad was
 * worse: it pairs people who then do not turn up. Played since mostly in Tromsø, Vadsø and
 * Hammerfest. The description this follows is https://www.tromsosjakk.no/2000/konradsystemet.htm
 *
 * It is Monrad with the rules that club nights break taken out:
 *
 *  - Whoever is there plays. A round is paired from the players present shortly before play
 *    starts, so nobody is ever paired against someone who does not come.
 *  - A player may sit a round out (frirunde) and never has to play it later. The organiser
 *    decides what that is worth — a full point, half a point or nothing — and for how many
 *    rounds. Never in the final round: sitting the last round out to protect a placing is the
 *    speculation the rule exists to prevent, so an absence there always scores nothing.
 *  - Two players may meet more than once (normally twice), with the colours reversed from
 *    their previous game. A club field is wide, and without this a leader ends up paired
 *    against the bottom of the table late in the event.
 *  - Optionally only each player's best N rounds count (e.g. 7 of 9), which rewards turning
 *    up without punishing the odd evening away. That is a standings rule, so it lives in the
 *    tie-break engine (js/core/tiebreak.js, `countedRounds`), not here.
 *
 * The pairing itself is MonradEngine (js/core/swiss-engine.js) with its Konrad options
 * switched on. What this file adds is the rest: the byes for absent players, and telling the
 * caller which pairings meet more often than the settings allow.
 *
 * Settings are the normalised shape from konradSettingsFrom() in js/state.js:
 *   { maxMeetings, byeValue: 'full'|'half'|'zero', maxScoringByes (0 = no limit), countedRounds }
 */
(function (root) {
    'use strict';

    // What a sat-out round is recorded as: the player on the white side against 'BYE', the
    // same shape as every other bye in the app, so standings and tie-breaks read it without
    // special cases. It carries `requestedBye: true` because that shape alone cannot tell it
    // from the odd-player bye the pairing allocates, and two things must: the TRF export
    // (F for a full-point bye the player took, U for an allocated one), and Monrad's §12A,
    // under which sitting out by choice must not use up anyone's allocated bye.
    const BYE_RESULT = { full: '1-0', half: '0.5-0.5' };

    // Results that gave the white side something, for a BYE pairing an arbiter may have
    // edited by hand afterwards. Not '-/-': a double forfeit scores nothing for either side.
    const SCORING_FOR_WHITE = new Set(['1-0', '+/-', '0.5-0.5']);

    // A forfeited game was never played, so it is not a meeting (same rule as the pairing
    // engines — see isPlayedGame in js/core/swiss-engine.js).
    const UNPLAYED = new Set(['+/-', '-/+', '-/-']);

    const isReal = (id) => id && id !== 'WO' && id !== 'BYE';

    /** How many of a player's sat-out rounds so far were worth points. */
    function scoringByesTaken(playerId, rounds) {
        let n = 0;
        rounds.forEach(r => (r.pairings || []).forEach(p => {
            if (p.requestedBye && p.white === playerId && SCORING_FOR_WHITE.has(p.result)) n++;
        }));
        return n;
    }

    /** How many games two players have played against each other. */
    function meetingCount(a, b, rounds) {
        let n = 0;
        rounds.forEach(r => (r.pairings || []).forEach(p => {
            if (UNPLAYED.has(p.result)) return;
            if ((p.white === a && p.black === b) || (p.white === b && p.black === a)) n++;
        }));
        return n;
    }

    /**
     * The bye pairings for the players who are not here this round.
     *
     * Only the absences that earn something get a pairing. One that earns nothing — the
     * final round, a bye worth zero, or a player who has used up their scoring byes — is left
     * out entirely, which is how the rest of the app already records a zero-point absence
     * (the TRF export writes it as Z).
     */
    function byePairings(players, rounds, settings, totalRounds) {
        const result = BYE_RESULT[settings.byeValue];
        if (!result) return [];
        const roundNo = rounds.length + 1;
        if (totalRounds > 0 && roundNo >= totalRounds) return [];
        const cap = settings.maxScoringByes || 0;
        return players
            .filter(p => !p.status)
            .filter(p => !cap || scoringByesTaken(p.id, rounds) < cap)
            .map(p => ({ white: p.id, black: 'BYE', result, requestedBye: true }));
    }

    /**
     * Pair the next round.
     *
     * @param {Array}  players  the whole field; `status` true means present tonight
     * @param {Array}  rounds   the rounds played so far
     * @param {Object} opts     { pointSystem, seedColor (resolved, not 'random'), totalRounds, settings }
     * @returns {{ pairings: Array, overCap: Array<[string, string]> }} — overCap lists the
     *   pairs meeting more often than settings.maxMeetings allows. The engine only produces
     *   those when no other pairing exists, and the arbiter should be asked before accepting.
     */
    function generateRound(players, rounds, opts) {
        const settings = opts.settings;
        const totalRounds = opts.totalRounds || 0;

        const paired = root.MonradEngine.generatePairings(
            players, rounds, opts.pointSystem, opts.seedColor, totalRounds,
            { maxMeetings: settings.maxMeetings, reverseColoursOnRematch: true, pairingByesOnly: true }
        );

        const overCap = paired
            .filter(p => isReal(p.white) && isReal(p.black)
                && meetingCount(p.white, p.black, rounds) >= settings.maxMeetings)
            .map(p => [p.white, p.black]);

        return {
            pairings: [...paired, ...byePairings(players, rounds, settings, totalRounds)],
            overCap
        };
    }

    const KonradEngine = { generateRound, byePairings, scoringByesTaken, meetingCount };

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = { KonradEngine };
    }
    root.KonradEngine = KonradEngine;
})(typeof window !== 'undefined' ? window : globalThis);
